import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Tag, Barcode, Printer, Zap, Save, Package, Calendar
} from 'lucide-react';
import { useSharedStore } from '../../hooks/useSharedStore';
import {
  catalogProductsOf, effectiveBranchesOf, stockRowsOf, updateStockRow, updateCatalogProduct
} from '../../data/store';
import { generateUniqueStoreBarcode } from './ProductsTab';
import { generateCode128Svg } from '../../components/StickyLabelPrintModal';
import { CustomSelect } from '../../components/CustomSelect';

export const getTodayBatchCode = () => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`; // e.g. '261023' (6-digit YYMMDD format like '261011')
};

export const formatBatchNumber = (val) => {
  if (!val) return '';
  let clean = String(val).replace(/^(batch|b)[-:\s]*/i, '').trim();
  // Strip '20' from 8-digit date 20YYMMDD -> 6-digit YYMMDD (e.g. 20261023 -> 261023, matching 261011 format)
  clean = clean.replace(/^20(\d{6})/i, '$1');
  return clean || '';
};

// Generates daily date-based produce barcode: YYMMDD + 3-digit product code (e.g. 261024001)
export const generateDailyBatchBarcode = (activeProduct, allProducts = []) => {
  const datePrefix = getTodayBatchCode(); // e.g. '261024'
  if (!activeProduct) return `${datePrefix}001`;

  // If active product has an established numeric barcode, use last 3 digits
  const existingCode = String(activeProduct.barcode || '').trim();
  if (/^\d{3,14}$/.test(existingCode)) {
    const suffix = existingCode.slice(-3);
    return `${datePrefix}${suffix}`;
  }

  // Otherwise derive from catalog index (001, 002, 003...)
  const idx = allProducts.findIndex((p) => p.id === activeProduct.id);
  const num = String(idx >= 0 ? idx + 1 : 1).padStart(3, '0');
  return `${datePrefix}${num}`;
};

export const BarcodeLabelsTab = ({ org, showNotification, onNavigate }) => {
  useSharedStore();
  const orgId = org?.id;
  const accent = org?.theme?.primary || '#3b82f6';
  const branches = useMemo(() => effectiveBranchesOf(orgId), [orgId]);
  const allProducts = useMemo(() => catalogProductsOf(orgId) || [], [orgId]);

  // Selected branch
  const [selectedBranchId, setSelectedBranchId] = useState(() => branches[0]?.id || '');
  const selectedBranch = useMemo(
    () => branches.find((b) => b.id === selectedBranchId) || branches[0],
    [branches, selectedBranchId]
  );

  // Selected product & variant (starts empty so user explicitly chooses product)
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [selectedStockRowId, setSelectedStockRowId] = useState('');

  // Currently active product
  const activeProduct = useMemo(
    () => (selectedProductId ? allProducts.find((p) => p.id === selectedProductId) || null : null),
    [allProducts, selectedProductId]
  );

  // Available variants for the active product
  const variants = useMemo(() => {
    if (!activeProduct) return [];
    return Array.isArray(activeProduct.variants) ? activeProduct.variants : [];
  }, [activeProduct]);

  // All active branch stock rows for this product (across all variants and unvarianted)
  const productBranchStock = useMemo(() => {
    if (!activeProduct || !selectedBranchId) return [];
    return stockRowsOf(orgId, selectedBranchId, 'active').filter((r) => r.productId === activeProduct.id);
  }, [orgId, selectedBranchId, activeProduct]);

  // Auto-select the variant that actually has branch stock (or first available variant)
  useEffect(() => {
    if (!activeProduct) {
      setSelectedVariantId('');
      setSelectedStockRowId('');
      return;
    }
    const prodVariants = Array.isArray(activeProduct.variants) ? activeProduct.variants : [];
    const prodRows = stockRowsOf(orgId, selectedBranchId, 'active').filter((r) => r.productId === activeProduct.id);

    // 1. Is there an active stock row with quantity > 0?
    const inStockRow = prodRows.find((r) => Number(r.qty) > 0);
    if (inStockRow) {
      setSelectedVariantId(inStockRow.variantId || '');
      setSelectedStockRowId(inStockRow.id);
      return;
    }

    // 2. Check if any stock row exists at all
    if (prodRows.length > 0) {
      setSelectedVariantId(prodRows[0].variantId || '');
      setSelectedStockRowId(prodRows[0].id);
      return;
    }

    // 3. Fallback to first variant if defined
    if (prodVariants.length > 0) {
      setSelectedVariantId(prodVariants[0].id);
    } else {
      setSelectedVariantId('');
    }
    setSelectedStockRowId('');
  }, [activeProduct?.id, selectedBranchId, orgId]);

  // Current active variant object (or null for base/unvarianted)
  const activeVariant = useMemo(() => {
    if (!selectedVariantId || selectedVariantId === 'base') return null;
    return variants.find((v) => v.id === selectedVariantId) || null;
  }, [variants, selectedVariantId]);

  // Available stock rows in this branch for the selected product & variant
  const branchStockRows = useMemo(() => {
    if (!activeProduct || !selectedBranchId) return [];
    return stockRowsOf(orgId, selectedBranchId, 'active').filter((r) => {
      const matchProd = r.productId === activeProduct.id;
      const matchVar = (!selectedVariantId || selectedVariantId === 'base')
        ? (!r.variantId || r.variantId === 'base' || r.variantId === '')
        : r.variantId === selectedVariantId;
      return matchProd && matchVar;
    });
  }, [orgId, selectedBranchId, activeProduct, selectedVariantId]);

  const activeStock = useMemo(() => {
    if (selectedStockRowId) {
      return branchStockRows.find((r) => r.id === selectedStockRowId) || branchStockRows[0] || null;
    }
    return branchStockRows[0] || null;
  }, [branchStockRows, selectedStockRowId]);

  const totalBranchStockQty = useMemo(() => {
    return branchStockRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  }, [branchStockRows]);

  // Label Configuration
  const [labelSize, setLabelSize] = useState('50x30'); // '50x30' or '40x25'
  const [printQty, setPrintQty] = useState(1);
  const [customBarcode, setCustomBarcode] = useState('');
  const [customBatch, setCustomBatch] = useState('');
  const [customExpiry, setCustomExpiry] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Derived item price from variant or base product
  const itemPrice = useMemo(() => {
    if (!activeProduct) return 0;
    if (activeVariant?.price !== undefined && Number(activeVariant.price) > 0) {
      return Number(activeVariant.price);
    }
    return Number(activeProduct.basePrice || 0);
  }, [activeProduct, activeVariant]);

  // Resolved product image (prioritizing activeVariant -> activeProduct.images -> sibling variants)
  const productImage = useMemo(() => {
    if (!activeProduct) return null;
    return (
      activeVariant?.images?.[0] ||
      activeProduct.images?.[0] ||
      activeProduct.image ||
      (activeProduct.variants || []).find((v) => v.images && v.images.length > 0)?.images?.[0] ||
      null
    );
  }, [activeProduct, activeVariant]);

  // Sync form inputs when product, variant, or active stock changes
  useEffect(() => {
    if (!activeProduct) {
      setCustomBarcode('');
      setCustomBatch('');
      setCustomExpiry('');
      return;
    }

    // 1. Barcode: prioritize activeStock.barcode -> activeVariant.barcode -> activeProduct.barcode
    const existingBarcode = activeStock?.barcode || activeVariant?.barcode || activeProduct?.barcode || '';
    setCustomBarcode(existingBarcode);

    // 2. Batch Number: clean format without duplicate "Batch" prefix
    if (activeStock?.batchNumber) {
      setCustomBatch(formatBatchNumber(activeStock.batchNumber));
    } else {
      setCustomBatch('');
    }

    // 3. Expiry: prioritize activeStock.expiryDate
    if (activeStock?.expiryDate) {
      setCustomExpiry(activeStock.expiryDate);
    } else if (activeProduct.shelfLifeDays && Number(activeProduct.shelfLifeDays) > 0) {
      const exp = new Date();
      exp.setDate(exp.getDate() + Number(activeProduct.shelfLifeDays));
      setCustomExpiry(exp.toISOString().slice(0, 10));
    } else {
      setCustomExpiry('');
    }
  }, [activeProduct, activeVariant, activeStock]);

  // Handle Auto-generate daily batch barcode (YYMMDD + Product Sequence)
  const handleAutoGenerate = () => {
    const nextCode = generateDailyBatchBarcode(activeProduct, allProducts);
    setCustomBarcode(nextCode);
    if (!customBatch) {
      setCustomBatch(getTodayBatchCode());
    }
    if (showNotification) {
      showNotification(`Generated daily batch barcode: ${nextCode}`, 'success');
    }
  };

  // Auto-generate sequential daily batch barcodes for all variants in one click
  const handleAutoGenerateAllVariants = async () => {
    if (!activeProduct || variants.length === 0) return;
    const datePrefix = getTodayBatchCode();
    const todayBatch = getTodayBatchCode();

    const prodIdx = allProducts.findIndex((p) => p.id === activeProduct.id);
    const prodNum = String(prodIdx >= 0 ? prodIdx + 1 : 1).padStart(2, '0');

    // Create sequential codes for all variants
    const updatedVariants = variants.map((v, i) => {
      const seq = String(i + 1).padStart(2, '0');
      const vBarcode = `${datePrefix}${prodNum}${seq}`;
      return {
        ...v,
        barcode: vBarcode,
      };
    });

    try {
      // 1. Update active product variants in catalog
      await updateCatalogProduct(activeProduct.id, { variants: updatedVariants });

      // 2. Also update any active branch stock rows for each variant if they exist
      for (let i = 0; i < variants.length; i++) {
        const v = updatedVariants[i];
        const vStock = productBranchStock.find((r) => r.variantId === v.id);
        if (vStock) {
          await updateStockRow(vStock.id, {
            barcode: v.barcode,
            batchNumber: todayBatch,
          });
        }
      }

      // 3. Set current active variant barcode
      const activeIdx = variants.findIndex((v) => v.id === selectedVariantId);
      const targetSeq = activeIdx >= 0 ? String(activeIdx + 1).padStart(2, '0') : '01';
      const curBarcode = `${datePrefix}${prodNum}${targetSeq}`;
      setCustomBarcode(curBarcode);
      if (!customBatch) setCustomBatch(todayBatch);

      if (showNotification) {
        showNotification(`Generated sequential barcodes for all ${variants.length} variants!`, 'success');
      }
    } catch (e) {
      console.error('Failed to auto-generate all variants:', e);
      if (showNotification) showNotification('Failed to update all variant barcodes', 'error');
    }
  };

  // Save barcode & batch values: updates physical stock strictly in stock_rows, never mutates master catalog for daily sticky labels
  const handleSaveToBranchStock = async () => {
    if (!activeProduct || !selectedBranchId) return;
    setIsSaving(true);
    try {
      const trimmedBarcode = String(customBarcode).trim();

      // 1. If physical branch stock exists, update its barcode and details strictly in stock_rows
      if (activeStock) {
        await updateStockRow(activeStock.id, {
          barcode: trimmedBarcode,
          sellPrice: itemPrice,
          batchNumber: customBatch || activeStock.batchNumber || '',
          expiryDate: customExpiry || activeStock.expiryDate || '',
        });
      } else if (trimmedBarcode) {
        // 2. Only if the product has NO physical branch stock yet, register as the default master catalog code
        if (selectedVariantId && selectedVariantId !== 'base') {
          const updatedVariants = (activeProduct.variants || []).map((v) =>
            v.id === selectedVariantId ? { ...v, barcode: trimmedBarcode } : v
          );
          await updateCatalogProduct(activeProduct.id, { variants: updatedVariants });
        } else {
          await updateCatalogProduct(activeProduct.id, { barcode: trimmedBarcode });
        }
      }

      if (showNotification) {
        showNotification(
          activeStock
            ? `Barcode ${trimmedBarcode} updated for branch inventory stock!`
            : `Barcode ${trimmedBarcode} saved to product catalog!`,
          'success'
        );
      }
    } catch (e) {
      console.error('Failed to save barcode:', e);
      if (showNotification) showNotification(e.message || 'Failed to update barcode', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Direct Thermal Label Print
  const handlePrintLabels = async () => {
    if (!activeProduct) return;

    // Automatically sync to this branch's stock so cashier can scan it
    if (customBarcode) {
      await handleSaveToBranchStock();
    }

    const printWindow = window.open('', '_blank', 'width=650,height=650');
    if (!printWindow) {
      window.print();
      return;
    }

    const prodTitle = activeProduct.name || 'Product Item';
    const variantLabel = activeVariant?.label || '';
    const barcodeVal = String(customBarcode || '200001').trim();
    const isCompact = labelSize === '40x25';
    const barcodeSvg = generateCode128Svg(barcodeVal, isCompact ? 22 : 32);
    const numCopies = Math.max(1, parseInt(printQty, 10) || 1);

    const labelsHtml = Array.from({ length: numCopies }).map(() => `
      <div class="label-page">
        <div class="label-box">
          <div class="prod-name">${prodTitle.toUpperCase()}</div>
          ${variantLabel ? `<div class="variant-tag">${variantLabel.toUpperCase()} • ₹${itemPrice}</div>` : (itemPrice > 0 ? `<div class="variant-tag">₹${itemPrice}</div>` : '')}
          <div class="meta-row">
            <span>Batch: ${formatBatchNumber(customBatch)}</span>
            <span>Exp: ${customExpiry || '—'}</span>
          </div>
          <div class="barcode-wrap">${barcodeSvg}</div>
          <div class="barcode-num">${barcodeVal}</div>
        </div>
      </div>
    `).join('');

    const pageW = isCompact ? '40mm' : '50mm';
    const pageH = isCompact ? '25mm' : '30mm';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Labels - ${prodTitle}</title>
        <style>
          @page {
            size: ${pageW} ${pageH};
            margin: 0;
          }
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
            background: #fff;
            color: #000;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .label-page {
            width: ${pageW};
            height: ${pageH};
            page-break-after: always;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .label-box {
            width: 100%;
            height: 100%;
            padding: ${isCompact ? '1mm 1.5mm' : '1.5mm 2mm'};
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            text-align: center;
          }
          .prod-name {
            font-size: ${isCompact ? '6.5pt' : '8pt'};
            font-weight: 900;
            line-height: 1.1;
            text-transform: uppercase;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .variant-tag {
            display: block;
            margin: 0.3mm auto 0;
            font-size: ${isCompact ? '5.5pt' : '6.5pt'};
            font-weight: 700;
            text-transform: uppercase;
            color: #111;
            line-height: 1.2;
          }
          .meta-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: ${isCompact ? '4.5pt' : '5.5pt'};
            font-weight: 700;
            color: #222;
            padding: 0 0.5mm;
            line-height: 1;
          }
          .meta-row span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 48%;
          }
          .price-val {
            font-size: ${isCompact ? '8.5pt' : '11pt'};
            font-weight: 900;
            line-height: 1;
            text-align: center;
          }
          .barcode-wrap {
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            width: 100%;
          }
          .barcode-wrap svg {
            width: 100%;
            max-width: ${isCompact ? '32mm' : '42mm'};
            height: ${isCompact ? '4.5mm' : '6.5mm'};
          }
          .barcode-num {
            font-family: monospace;
            font-size: ${isCompact ? '5.5pt' : '7.5pt'};
            font-weight: 700;
            letter-spacing: 1px;
            line-height: 1;
            text-align: center;
          }
        </style>
      </head>
      <body>
        ${labelsHtml}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 600);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // CustomSelect Options for Products with search, image/icon & clean barcode numbers
  const productOptions = useMemo(() => {
    return allProducts.map((p) => {
      const varCodes = (p.variants || []).map((v) => v.barcode).filter(Boolean);
      const codeOnly = p.barcode || (varCodes.length > 0 ? varCodes[0] : '');
      const pImg = p.images?.[0] || p.image || (p.variants || []).find((v) => v.images?.[0])?.images?.[0] || null;

      return {
        value: p.id,
        label: p.name,
        subtext: codeOnly,
        icon: pImg ? (
          <img src={pImg} alt={p.name} className="w-5 h-5 rounded object-cover border border-slate-200 shrink-0" />
        ) : (
          <div className="w-5 h-5 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-slate-400">
            <Package size={11} />
          </div>
        ),
      };
    });
  }, [allProducts]);

  // CustomSelect Options for Variants (Clean: label only, no price, no barcode)
  const variantOptions = useMemo(() => {
    if (!activeProduct || !Array.isArray(activeProduct.variants) || activeProduct.variants.length === 0) {
      return [];
    }
    return activeProduct.variants.map((v) => ({
      value: v.id,
      label: v.label,
    }));
  }, [activeProduct]);

  // CustomSelect Options for Branches
  const branchOptions = useMemo(() => {
    return branches.map((b) => ({
      value: b.id,
      label: b.name,
    }));
  }, [branches]);

  // CustomSelect Options for Batches
  const batchOptions = useMemo(() => {
    return branchStockRows.map((r, i) => ({
      value: r.id,
      label: `Batch ${r.batchNumber || `#${i + 1}`} (${r.qty} on shelf)`,
      badge: r.expiryDate ? `Exp: ${r.expiryDate}` : undefined,
    }));
  }, [branchStockRows]);

  return (
    <div className="w-full h-full flex flex-col overflow-y-auto custom-scrollbar p-1 pb-8">
      {/* Single Unified Studio Card */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-7">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Product & Barcode Configuration (7 Cols) */}
          <div className="lg:col-span-7 lg:border-r lg:border-slate-100 lg:pr-8 space-y-5">
            {/* Branch Selector Header if multi-branch */}
            {branches.length > 1 && (
              <div className="flex items-center justify-between pb-0.5">
                <span className="text-[11px] font-bold text-slate-500">Branch</span>
                <div className="w-56">
                  <CustomSelect
                    value={selectedBranchId}
                    onChange={setSelectedBranchId}
                    options={branchOptions}
                    placeholder="Select branch..."
                    className="bg-slate-50 border-slate-200 text-xs font-bold normal-case tracking-normal rounded-xl h-9"
                  />
                </div>
              </div>
            )}

            {/* Single Line: Select Product & Select Variant Side-by-Side */}
            <div className={`grid gap-3 items-start ${activeProduct && variantOptions.length > 0 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
              {/* Field 1: Select Product */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block">
                  Select Product
                </label>
                <CustomSelect
                  value={selectedProductId}
                  onChange={setSelectedProductId}
                  options={productOptions}
                  searchable
                  showBadgeInButton={false}
                  searchPlaceholder="Search product by name or barcode..."
                  placeholder="Select product..."
                  className="bg-slate-50 border-slate-200 text-xs font-bold normal-case tracking-normal rounded-lg h-10 w-full"
                />
              </div>

              {/* Field 2: Individual Variant Dropdown (Same Line) */}
              {activeProduct && variantOptions.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 block">
                      Select Variant
                    </label>
                    {variantOptions.length > 1 && (
                      <button
                        type="button"
                        onClick={handleAutoGenerateAllVariants}
                        className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer transition-colors"
                        title="Auto-generate barcodes for all variants"
                      >
                        <Zap size={11} className="text-slate-500" />
                        <span>Auto-Gen All</span>
                      </button>
                    )}
                  </div>

                  <CustomSelect
                    value={selectedVariantId || (variantOptions[0]?.value || '')}
                    onChange={(val) => {
                      setSelectedVariantId(val);
                      const vStockRow = productBranchStock.find((r) => r.variantId === val);
                      setSelectedStockRowId(vStockRow?.id || '');
                    }}
                    options={variantOptions}
                    placeholder="Select variant..."
                    className="bg-slate-50 border-slate-200 text-xs font-bold normal-case tracking-normal rounded-lg h-10 w-full"
                  />
                </div>
              )}
            </div>

          {/* Barcode input with docked Auto-Gen button (styled like edit button in inventory active) */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 block">
              Barcode Number
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={customBarcode}
                onChange={(e) => setCustomBarcode(e.target.value)}
                placeholder="Enter or scan barcode..."
                className="w-full h-10 px-3 pr-24 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-900 focus:bg-white focus:border-slate-400 focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={handleAutoGenerate}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 px-2 rounded text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 shadow-2xs hover:shadow-xs active:scale-95 flex items-center gap-1 transition-all cursor-pointer group"
                title="Generate daily batch barcode (YYMMDD + Item Code)"
              >
                <Zap size={10} className="text-slate-500 group-hover:text-slate-800 transition-colors" />
                <span>AUTO-GEN</span>
              </button>
            </div>
          </div>

          {/* Batch & Expiry side-by-side */}
          <div className="grid grid-cols-2 gap-3 pt-0.5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 block">Batch No</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={customBatch}
                  onChange={(e) => setCustomBatch(e.target.value)}
                  placeholder="e.g. 261027"
                  className="w-full h-10 px-3 pr-20 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-900 focus:bg-white focus:border-slate-400 focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setCustomBatch(getTodayBatchCode())}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 px-2 rounded text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 shadow-2xs hover:shadow-xs active:scale-95 flex items-center gap-1 transition-all cursor-pointer group"
                  title="Set batch number to today (e.g. 261027)"
                >
                  <Calendar size={10} className="text-slate-500 group-hover:text-slate-800 transition-colors" />
                  <span>TODAY</span>
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 block">Expiry Date</label>
              <input
                type="date"
                value={customExpiry}
                onChange={(e) => setCustomExpiry(e.target.value)}
                className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:bg-white focus:border-slate-400 focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Sticker Preview & Actions (5 Cols) */}
        <div className="lg:col-span-5 space-y-5 flex flex-col justify-between">
            <div className="w-full flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-slate-700">
                Sticker Preview
              </span>

              {/* Thermal Paper Size Toggle */}
              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                <button
                  type="button"
                  onClick={() => setLabelSize('50x30')}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer ${
                    labelSize === '50x30'
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200 font-extrabold'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  50 × 30 mm
                </button>
                <button
                  type="button"
                  onClick={() => setLabelSize('40x25')}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer ${
                    labelSize === '40x25'
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200 font-extrabold'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  40 × 25 mm
                </button>
              </div>
            </div>

            {/* Sticker Preview Stage (Framed stage container matching demo) */}
            <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-center min-h-[185px]">
              <div
                style={{
                  width: labelSize === '50x30' ? '240px' : '216px',
                  height: labelSize === '50x30' ? '144px' : '135px',
                }}
                className={`bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col justify-between text-center select-none transition-all duration-200 relative overflow-hidden ${
                  labelSize === '40x25' ? 'p-2.5' : 'p-3'
                }`}
              >
                <div>
                  <div className={`font-black uppercase tracking-wide text-slate-950 line-clamp-1 leading-tight ${
                    labelSize === '40x25' ? 'text-[11px]' : 'text-[13px]'
                  }`}>
                    {activeProduct ? activeProduct.name.toUpperCase() : 'SELECT PRODUCT'}
                  </div>
                  {activeVariant?.label ? (
                    <div className={`font-bold text-slate-700 mt-0.5 ${
                      labelSize === '40x25' ? 'text-[8.5px]' : 'text-[9.5px]'
                    }`}>
                      {activeVariant.label} • ₹{itemPrice}
                    </div>
                  ) : activeProduct && itemPrice > 0 ? (
                    <div className={`font-bold text-slate-700 mt-0.5 ${
                      labelSize === '40x25' ? 'text-[8.5px]' : 'text-[9.5px]'
                    }`}>
                      ₹{itemPrice}
                    </div>
                  ) : null}
                </div>

                <div className={`flex items-center justify-between font-bold text-slate-600 px-1 ${
                  labelSize === '40x25' ? 'text-[7.5px]' : 'text-[8.5px]'
                }`}>
                  <span className="truncate max-w-[48%] text-left">Batch: {activeProduct && customBatch ? formatBatchNumber(customBatch) : '—'}</span>
                  <span className="truncate max-w-[48%] text-right">Exp: {customExpiry || '—'}</span>
                </div>

                {/* Vector Barcode Render */}
                <div className="flex flex-col items-center justify-center">
                  <div
                    className={`w-full mx-auto flex items-center justify-center overflow-hidden ${
                      labelSize === '40x25' ? 'max-w-[150px] h-6' : 'max-w-[190px] h-7'
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: generateCode128Svg(customBarcode || (activeProduct ? '200001' : '000000'), labelSize === '40x25' ? 24 : 28),
                    }}
                  />
                  <div className={`font-mono font-black tracking-widest text-slate-900 text-center ${
                    labelSize === '40x25' ? 'text-[8px] mt-0.5' : 'text-[9.5px] mt-0.5'
                  }`}>
                    {customBarcode || (activeProduct ? '200001' : '—')}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Bar: Quantity Stepper (Left) + Print Button (Right) */}
            <div className="w-full pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-slate-700 tracking-wider">QTY:</span>
                <div className="h-8 flex items-center border border-slate-200 rounded-md overflow-hidden bg-white shadow-xs shrink-0">
                  <button
                    type="button"
                    onClick={() => setPrintQty((q) => Math.max(1, q - 1))}
                    className="w-7 h-8 flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 text-xs font-bold cursor-pointer select-none border-r border-slate-200 transition-colors"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={printQty}
                    onChange={(e) => setPrintQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-10 h-8 bg-transparent text-xs font-bold text-center text-slate-900 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setPrintQty((q) => q + 1)}
                    className="w-7 h-8 flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 text-xs font-bold cursor-pointer select-none border-l border-slate-200 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handlePrintLabels}
                disabled={!activeProduct || isSaving}
                className="h-8 px-4 rounded-md text-white font-bold text-xs shadow-xs hover:shadow transition-all flex items-center justify-center gap-1.5 active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: accent, boxShadow: `0 3px 8px -2px ${accent}60` }}
              >
                <Printer size={13} />
                <span>Print</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BarcodeLabelsTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  onNavigate: PropTypes.func,
};
